import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Page } from 'common/components/Page';
import { getOptionLabelDefaultCallback, isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { DEFAULT_APP_SUITE, DEFAULT_TENANT,USER_GROUP_ACLS_REPORT_SLUG, USER_GROUP_ACLS_REPORT_TITLE } from 'common/constants/reports';
import { Routes } from 'common/constants/routes';
import { fetchApplicationSuites } from 'common/services/api/ApplicationSuites/ApplicationSuites.thunks';
import { ApplicationSuite,AppSuiteTenant } from 'common/types/Entities';
import { displayAppSuiteOptionMenuItemContent } from 'common/utils/AppSuiteEntitiesDropDownHelper';
import { useGenerateDownloadableCSV } from 'features/reports/hooks/common/useGenerateDownloadableCSV';
import { useProcessFetchUserGroupsAclsReportData } from 'features/reports/hooks/userGroupAclReportHooks/useProcessFetchUserGroupsAclsReportData';
import { useProcessGenerateReportButtonState } from 'features/reports/hooks/userGroupAclReportHooks/useProcessGenerateReportButtonState';
import { useSnackbar } from 'notistack';

import { TenantSearchableDropDown } from '../../../../common/components/TenantSearchableDropDown';
import { selectIsTenantDropDownDisabled,setTenantDropDownDisabled } from '../../tenantSearchableDropDownSlice';
import { CsvReportExportPopupLoader } from '../common/CsvReportExportPopupLoader';

const USER_GROUP_ACLS_REPORT_FILENAME = 'UserGroupAclsReport';
const USER_GROUP_ACLS_REPORT_ERROR_MESSAGE_PREFIX = 'There was an error while generating User Group Acls CSV Report:';
const DEFAULT_POPUP_LOADER_MESSAGE = 'Exporting...';

export function UserGroupAclsReport() {
    const [appSuite, setAppSuite] = useState<ApplicationSuite | null>(null);
    const [tenant, setTenant] = useState<AppSuiteTenant | null>(null);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isTenantSearchableDropDownDisabled = useAppSelector(selectIsTenantDropDownDisabled);

    const { enqueueSnackbar } = useSnackbar();

    const { disableGenerateButton }  = useProcessGenerateReportButtonState(appSuite, tenant);

    useEffect(() => {
        dispatch(setTenantDropDownDisabled(true));
    }, []);

    useEffect(() => {
        if(appSuite?.id) {
            dispatch(setTenantDropDownDisabled(false));
            setTenant(null);
        }
    }, [appSuite?.id]);

    const {
        processing,
        processFetchUserGroupsAclsReportData
    } = useProcessFetchUserGroupsAclsReportData(tenant?.id);

    const { convertToCSV, downloadCSV } = useGenerateDownloadableCSV(USER_GROUP_ACLS_REPORT_FILENAME);

    const handleClickCancel = () => {
        navigate(`/${Routes.Reports}`);
    };

    const handleGenerateReport = async () => {
        try {
            const reportData = (await processFetchUserGroupsAclsReportData()) ?? [];
            const csv = await convertToCSV(reportData);
            downloadCSV(csv);
        } catch (error: unknown) {
            const err = error as Error;
            enqueueSnackbar(
                `${USER_GROUP_ACLS_REPORT_ERROR_MESSAGE_PREFIX} ${err.message}`,
                { variant: 'error' }
            );
        }
    };

    const handleApplicationSuiteChange = (_e: unknown, newAppSuite: ApplicationSuite | null) => {
        setAppSuite(newAppSuite);
    };

    const handleTenantChange = (_e: unknown, newTenant: AppSuiteTenant | null) => {
        setTenant(newTenant);
    };

    return (
        <Page
            title={`${USER_GROUP_ACLS_REPORT_TITLE} Report`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    <StyledBreadcrumbs
                        numberOfTabsToExcludeFromLinks={1}
                        guidNameMappings={[{
                            resourceName: USER_GROUP_ACLS_REPORT_TITLE,
                            resourceId: USER_GROUP_ACLS_REPORT_SLUG
                        }]}
                    />
                    {`${USER_GROUP_ACLS_REPORT_TITLE} Report`}
                </Typography>
            )}
        >
            <Stack
                sx={{ paddingTop: '1rem', width: '30%' }}
                spacing={2}
            >
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={DEFAULT_APP_SUITE}
                    value={appSuite}
                    onChange={handleApplicationSuiteChange}
                    label='Application Suite'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchApplicationSuites}
                    asyncThunkArgs={undefined}
                    displayMenuItemContent={displayAppSuiteOptionMenuItemContent}
                />
                <TenantSearchableDropDown
                    appSuite={appSuite}
                    value={tenant ?? DEFAULT_TENANT}
                    onChange={handleTenantChange}
                    disabled={isTenantSearchableDropDownDisabled}
                />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant={'contained'}
                        sx={{ marginRight: '1rem' }}
                        onClick={handleGenerateReport}
                        disabled={disableGenerateButton}
                    >
                        Generate
                    </Button>
                    <Button
                        variant={'outlined'}
                        onClick={handleClickCancel}
                    >
                        Cancel
                    </Button>
                </div>
            </Stack>
            <CsvReportExportPopupLoader
                open={processing}
                title={DEFAULT_POPUP_LOADER_MESSAGE}
            />
        </Page>
    );
}
